import clsx from "clsx";
import { Button } from "components/Theme/button";
import { useFeatureAcknowledgement } from "hooks/useFeatureAcknowledgement";
import type React from "react";

interface FeatureAnnouncementBannerProps {
  /** Unique identifier for the feature in format YYYY-MM-DD-feature-name (e.g., "2026-01-15-export-units") */
  featureId: string;
  /** Message content to display in the banner */
  message: string | React.ReactNode;
  /** Optional additional CSS classes */
  className?: string;
  /** Optional margin size */
  margin?: 0 | 1 | 2 | 3 | 4;
}

const marginStyles = {
  0: "",
  1: "my-1",
  2: "my-2",
  3: "my-3",
  4: "my-4",
};

/**
 * A dismissible feature announcement banner that persists dismissal state per user.
 *
 * The banner will display for users who have not yet acknowledged the feature.
 * Clicking "Got it" will immediately hide the banner and persist the acknowledgement
 * to the user's profile.
 *
 * @example
 * <FeatureAnnouncementBanner
 *   featureId="2026-01-15-export-units"
 *   message="We've updated this feature to support units."
 * />
 */
export function FeatureAnnouncementBanner({
  featureId,
  message,
  className,
  margin = 0,
}: FeatureAnnouncementBannerProps): React.ReactElement | null {
  const { isAcknowledged, acknowledge, isProfileLoading } =
    useFeatureAcknowledgement(featureId);

  // Don't render while checking acknowledgement status (avoid flash)
  if (isProfileLoading) {
    return null;
  }

  // Don't render if already acknowledged
  if (isAcknowledged) {
    return null;
  }

  return (
    <div
      className={clsx(
        "pointer-events-auto flex items-center justify-between gap-x-6 bg-blue-600 px-6 py-2.5 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-3.5",
        marginStyles[margin],
        className,
      )}
    >
      <span className="text-sm leading-6 text-white">{message}</span>
      <Button
        color="light"
        size="sm"
        onClick={acknowledge}
        className="flex-none"
      >
        Got it
      </Button>
    </div>
  );
}
